import { useEffect, useRef, useState } from 'react';
import { Box, TextField, Button, Alert, Typography, LinearProgress, MenuItem } from '@mui/material';
import { fetchCaptureContext, CaptureContextResponse } from '../services/captureContextService';
import { useAuthTokenContext } from '../context/AuthTokenContext';
import { useQueryParamsContext } from '../context/QueryParamsContext';
import { useTranslation } from '../i18n/translations';
import { FormField } from './FormField';
import { useVerifyAndEnroll } from '../hooks/useVerifyAndEnroll';

// ---- Flex (Cybersource) decoded token typing ----
// ---- Legacy Flex decoded token typing retained for extraction helper (the service returns decodedPayload / decodedHeader) ----
interface FlexHeader { kid: string; alg: string; }
interface FlexJwk { kty: string; e: string; use: string; n: string; kid: string; }
interface FlexFlx { path: string; data: string; origin: string; jwk: FlexJwk; }
interface FlexCtxData { clientLibraryIntegrity?: string; clientLibrary?: string; allowedCardNetworks?: string[]; targetOrigins?: string[]; mfOrigin?: string; allowedPaymentTypes?: string[]; }
interface FlexCtxEntry { data: FlexCtxData; type: string; }
interface FlexPayload { flx: FlexFlx; ctx: FlexCtxEntry[]; iss: string; exp: number; iat: number; jti: string; }
export interface FlexDecodedToken { header: FlexHeader; payload: FlexPayload; captureContextJwtToken: string; }

async function loadFlexScript(clientLibrary: string, integrity?: string) {
  if (document.querySelector(`script[data-flex="${clientLibrary}"]`)) return;
  
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = clientLibrary;
    s.async = true;
    if (integrity) {
      s.integrity = integrity;
      s.crossOrigin = 'anonymous';
    }
    s.dataset.flex = clientLibrary;
    
    s.onload = () => {
      resolve();
    };
    
    s.onerror = (event) => {
      console.error('[Flex] Script load failed:', {
        event,
        url: s.src,
        integrity: s.integrity,
        crossOrigin: s.crossOrigin,
        readyState: (s as any).readyState,
        // Check if the script element was even added to DOM
        inDOM: document.head.contains(s)
      });
      
      // Try to fetch the script directly to see if it's a network issue
      fetch(clientLibrary, { method: 'HEAD' })
        .then(resp => console.log('[Flex] HEAD check:', resp.status, resp.headers))
        .catch(err => console.error('[Flex] HEAD check failed:', err));
      
      reject(new Error(`load fail ${clientLibrary} - check console for details`));
    };
    
    document.head.appendChild(s);
  });
}

// Extract microform client library & integrity supporting both legacy (top-level ctx) and new (payload.ctx) shapes.
function extractMicroformClientInfo(decoded: FlexDecodedToken | FlexPayload | CaptureContextResponse) {
  let root: FlexPayload | undefined;
  if (decoded && typeof decoded === 'object') {
    if ('payload' in decoded && (decoded as any).payload?.ctx) {
      root = (decoded as FlexDecodedToken).payload;
    } else if ('decodedPayload' in decoded && (decoded as any).decodedPayload?.ctx) {
      root = (decoded as any).decodedPayload as FlexPayload;
    } else if ('ctx' in decoded) {
      root = decoded as FlexPayload;
    }
  }
  if (!root) {
    console.warn('Unable to locate Flex payload structure on capture-context response');
    return { clientLibrary: undefined, integrity: undefined };
  }
  const ctx0 = root?.ctx?.[0];
  const data = ctx0?.data || ctx0;
  if (!ctx0) {
    console.warn('Flex token ctx missing or empty. Received root:', root);
  }
  const clientLibrary = data?.clientLibrary;
  const integrity = data?.clientLibraryIntegrity;
  return { clientLibrary, integrity };
}

export function MicroformFlex() {
  // Get authToken and query params from context
  const { authToken, authError } = useAuthTokenContext();
  const { memberCardNumber, legacyAccountId, clientType, countryParam, locationNumber, userId } = useQueryParamsContext();
  const { t, locale } = useTranslation();
  
  // Use the shared verify hook
  const { verify, verifying, verifyResponse, verifyError, resetState } = useVerifyAndEnroll();
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [numberFocused, setNumberFocused] = useState(false);
  const [cvvFocused, setCvvFocused] = useState(false);
  const [numberValid, setNumberValid] = useState<boolean | null>(null);
  const [cvvValid, setCvvValid] = useState<boolean | null>(null);
  const [resultJson, setResultJson] = useState<any>(null);
  const [microformReady, setMicroformReady] = useState(false);
  const [country, setCountry] = useState<string>('');
  const [adminArea, setAdminArea] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string,string>>({});
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const US_STATES: { code: string }[] = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
  ].map(code => ({ code }));
  const CA_PROVINCES: { code: string }[] = [
    'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'
  ].map(code => ({ code }));

  const numberRef = useRef<HTMLDivElement>(null);
  const securityCodeRef = useRef<HTMLDivElement>(null);
  const microformInstanceRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [captureContextKey, setCaptureContextKey] = useState(0); // Force re-fetch capture context

  // Helper to get initial country from query param
  const getInitialCountry = () => {
    if (countryParam) {
      const upper = countryParam.toUpperCase();
      return upper === 'USA' ? 'US' : upper === 'CAN' ? 'CA' : upper;
    }
    return '';
  };

  // Query params are now loaded from context at app level
  useEffect(() => {
    // Set country from countryParam if present (preserve select normalization logic)
    if (countryParam && !country) {
      setCountry(getInitialCountry());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryParam]);

  // Clear field errors when locale changes so error messages update to new language
  useEffect(() => {
    setFieldErrors({});
  }, [locale]);

  useEffect(() => {
    if (!authToken) return; // Wait for authToken to be available
    let cancelled = false;
    
    // Reset validation states when country or locale changes to ensure clean re-initialization
    setCvvValid(null);
    setNumberValid(null);
    setFieldErrors({});
    
    (async () => {
      try {
        setLoading(true);
        setMicroformReady(false);
        const result = await fetchCaptureContext(country, authToken);
        if (cancelled) return;
        const { clientLibrary, integrity } = extractMicroformClientInfo(result);
        if (!clientLibrary) throw new Error('Missing microform clientLibrary in token payload');
        await loadFlexScript(clientLibrary, integrity);
        // Poll for Flex global up to ~2s
        const flexGlobal = await new Promise<any>((resolve, reject) => {
          const start = Date.now();
          (function poll(){
            if (cancelled) return;
            const Flex = (window as any).Flex;
            if (Flex) return resolve(Flex);
            if (Date.now() - start > 2000) return reject(new Error('Flex global not found after script load (timeout)'));
            setTimeout(poll, 50);
          })();
        });
        if (cancelled) return;
        const captureContextJwtToken = (result as any).captureContextJwtToken as string | undefined;
        if (!captureContextJwtToken || typeof captureContextJwtToken !== 'string') {
          throw new Error('captureContextJwtToken missing or invalid in response');
        }
        const flexRoot = new flexGlobal(captureContextJwtToken);
        microformInstanceRef.current = flexRoot.microform('card', { styles: {
          'input': {
            'font-size': '16px',
            'font-weight': '200',
            // Since the Costco Sans font file is hosted locally and the Cybersource iframe runs in a cross-origin context, 
            // it won't be able to load the font file directly. The iframe will fall back to "Segoe UI" or "Roboto"
            'font-family': '"Costco Sans", "Segoe UI", Roboto, sans-serif',
            'line-height': '1.4375em',
            'color': '#1e1e1e'
          },
          '::placeholder': {
            'color': '#a0a0a0'
          },
          ':focus': { 'color': '#1976d2' },
          'valid': { 'color': '#2e7d32' },
          'invalid': { 'color': '#d32f2f' }
        } });
        const mf = microformInstanceRef.current;
        
        let loadedFields = 0;
        const expectedFieldCount = 2; // Only card number if CVV is optional
        
        if (numberRef.current) {
          const f = mf.createField('number', { placeholder: t('cardNumber') });
          f.on('focus', () => setNumberFocused(true));
          f.on('blur', () => setNumberFocused(false));
          f.on('change', (e: any) => setNumberValid(e?.valid ?? null));
          f.on('load', () => { loadedFields++; if (loadedFields === expectedFieldCount) setMicroformReady(true); });
          f.load(numberRef.current);
        }
        
        // Only create CVV field if not optional (i.e., not Canada)
        if (securityCodeRef.current) {
          const f = mf.createField('securityCode', { placeholder: t('cvv') });
          f.on('focus', () => setCvvFocused(true));
          f.on('blur', () => setCvvFocused(false));
          f.on('change', (e: any) => setCvvValid(e?.valid ?? null));
          f.on('load', () => {
            loadedFields++;
            if (loadedFields === expectedFieldCount) {
              setMicroformReady(true);
            }
          });
          f.load(securityCodeRef.current);
        }
      } catch (e: any) { 
        if (!cancelled) {
          const translatedMessage = t('unknownError');
          const errorDetails = e.message ? `\n${e.message}` : '';
          setError(translatedMessage + errorDetails);
        }
      }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [authToken, country, captureContextKey, locale]);

  async function handleTokenize(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResultJson(null);
    resetState();
    
    try {
      const mf = microformInstanceRef.current;
      if (!mf) throw new Error('Microform not initialized');
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);

      // Collect non-hosted form values (for downstream processing; not sent to tokenization)
      const fields: Record<string, any> = {};
      ['expMonth','expYear','firstName','lastName','address1','address2','city','administrativeArea','country','postalCode']
        .forEach(f => { const v = formData.get(f); if (v) fields[f] = v.toString().trim(); });
      // Attach query-derived fields if present
      if (memberCardNumber) fields.memberCardNumber = memberCardNumber;
      if (legacyAccountId) fields.legacyAccountId = legacyAccountId;
      if (countryParam && !fields.country) fields.country = countryParam;

      // Validate required non-hosted fields prior to tokenization
      const requiredFields = ['expMonth','expYear','firstName','lastName','country','postalCode'];
      const newErrors: Record<string,string> = {};
      requiredFields.forEach(f => {
        const v = (formData.get(f) || '').toString().trim();
        if (!v) newErrors[f] = t('required');
      });
      if (!numberValid) newErrors['cardNumber'] = t('required');
      
      if (!cvvValid) {
        newErrors['cvv'] = t('required');
      }
      
      if (!microformReady) newErrors['microform'] = 'Loading';
      if (Object.keys(newErrors).length > 0) {
        setFieldErrors(newErrors);
        return;
      }
      setFieldErrors({});

      // Tokenize hosted card data (number + securityCode)
      const tokenOptions = {
        expirationMonth: fields.expMonth,
        expirationYear: fields.expYear
      };
      const tokenResponse = await new Promise<any>((resolve, reject) => {
        try {
          const maybe = mf.createToken(tokenOptions, (err: any, data: any) => {
            if (err) return reject(err);
            resolve(data);
          });
          if (maybe && typeof maybe.then === 'function') maybe.then(resolve).catch(reject);
        } catch (ex) { reject(ex); }
      });

      console.log('token response:', tokenResponse);
      setResultJson({ tokenResponse, fields });

      // Extract token value
      const tokenVal = (tokenResponse?.token || tokenResponse?.transientToken || tokenResponse?.tt || tokenResponse?.id || (typeof tokenResponse === 'string' ? tokenResponse : '')) as string;

      // Call verify-and-enroll using the shared hook
      const result = await verify({
        token: tokenVal,
        tokenIssuer: 'Cybersource',
        tokenType: 'CybersourceFlexTransientToken',
        firstName: fields.firstName,
        lastName: fields.lastName,
        postalCode: fields.postalCode,
        country: (fields.country || countryParam || country || '').toUpperCase(),
        memberCardNumber,
        legacyAccountId,
        locationNumber,
        userId
      }, authToken);

      if (result.success) {
        setPaymentSuccess(true);
      }
      
      // Always reset form after verify attempt
      if (formRef.current) {
        formRef.current.reset();
      }
      setCountry(getInitialCountry());
      setAdminArea('');
      setFieldErrors({});
      setNumberValid(null);
      setCvvValid(null);
      setResultJson(null);
      setCaptureContextKey(prev => prev + 1);
      
    } catch (e: any) {
      const translatedMessage = t('tokenizationFailed');
      const errorDetails = e.message ? `\n${e.message}` : '';
      setError(translatedMessage + errorDetails);
    }
  }

  return (
    <Box maxWidth={1000} component="section" sx={{ mt:2, mx:'auto', px:2 }}>
      {loading && <LinearProgress sx={{ mb:2 }} />}
      {verifying && <LinearProgress sx={{ mb:2 }} />}
      
      {/* Authentication error */}
      {authError && (
        <Alert severity="error" sx={{ mb:2 }}>
          {t('authenticationError')}
        </Alert>
      )}
      
      {/* Success message */}
      {verifyResponse && (
        <Alert severity="success" sx={{ mb:2 }}>
          {t('paymentMethodAddedSuccessfully')}
        </Alert>
      )}
      
      {/* Verification errors (payment/auto-renew) */}
      {verifyError && (
        <Alert severity="error" sx={{ mb:2 }}>
          {verifyError.split('\n').map((line, idx) => (
            <Box key={idx} component={idx === 0 ? 'div' : 'div'} sx={{ fontSize: idx === 0 ? 'inherit' : '0.85em', opacity: idx === 0 ? 1 : 0.8, mt: idx === 0 ? 0 : 0.5 }}>
              {line}
            </Box>
          ))}
        </Alert>
      )}
      
      {/* General errors (tokenization, unknown) */}
      {error && (
        <Alert severity="error" sx={{ mb:2 }}>
          {error.split('\n').map((line, idx) => (
            <Box key={idx} component={idx === 0 ? 'div' : 'div'} sx={{ fontSize: idx === 0 ? 'inherit' : '0.85em', opacity: idx === 0 ? 1 : 0.8, mt: idx === 0 ? 0 : 0.5 }}>
              {line}
            </Box>
          ))}
        </Alert>
      )}
      
      {/* Field validation errors */}
      {Object.keys(fieldErrors).length > 0 && (
        <Alert severity="error" sx={{ mb:2 }}>
          {t('pleaseFillRequiredFields')}: {Object.keys(fieldErrors).filter(k => !['microform'].includes(k)).join(', ')}
        </Alert>
      )}
      
      <Box 
        key={`form-${country}`} 
        ref={formRef} 
        component="form" 
        onSubmit={handleTokenize} 
        noValidate 
        sx={{
          display:'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap:2,
          opacity: (microformReady && !paymentSuccess) ? 1 : 0.55,
          pointerEvents: (microformReady && !paymentSuccess) ? 'auto' : 'none',
          transition: 'opacity .25s'
        }} 
        aria-busy={!microformReady} 
        aria-disabled={!microformReady || paymentSuccess}
      >
        {/* Card number takes full width if CVV is hidden (Canada), otherwise spans 5 columns */}
        <FormField 
          label={t('cardNumber')} 
          focus={numberFocused} 
          valid={numberValid} 
          span="span 5"
        >
          <Box ref={numberRef} sx={{ flex:1 }} />
        </FormField>
        {/* Only show CVV field for non-Canada countries */}
        <FormField label={t('cvv')} focus={cvvFocused} valid={cvvValid} span="span 1">
          <Box ref={securityCodeRef} sx={{ flex:1 }} />
        </FormField>
        <FormField label={t('expiryMonth')} span="span 3" input={<TextField disabled={loading} name="expMonth" placeholder={t('placeholderMM')} inputProps={{ maxLength:2 }} size="small" fullWidth required error={!!fieldErrors.expMonth} helperText={fieldErrors.expMonth || ''} onChange={() => fieldErrors.expMonth && setFieldErrors(p=>{ const c={...p}; delete c.expMonth; return c; })} />} />
        <FormField label={t('expiryYear')} span="span 3" input={<TextField disabled={loading} name="expYear" placeholder={t('placeholderYYYY')} inputProps={{ maxLength:4 }} size="small" fullWidth required error={!!fieldErrors.expYear} helperText={fieldErrors.expYear || ''} onChange={() => fieldErrors.expYear && setFieldErrors(p=>{ const c={...p}; delete c.expYear; return c; })} />} />
        <FormField label={t('firstName')} span="span 3" input={<TextField disabled={loading} name="firstName" size="small" fullWidth required error={!!fieldErrors.firstName} helperText={fieldErrors.firstName || ''} onChange={() => fieldErrors.firstName && setFieldErrors(p=>{ const c={...p}; delete c.firstName; return c; })} />} />
        <FormField label={t('lastName')} span="span 3" input={<TextField disabled={loading} name="lastName" size="small" fullWidth required error={!!fieldErrors.lastName} helperText={fieldErrors.lastName || ''} onChange={() => fieldErrors.lastName && setFieldErrors(p=>{ const c={...p}; delete c.lastName; return c; })} />} />
        {/* <FormField label="Billing Address 1" span="span 6" input={<TextField disabled={loading} name="address1" size="small" fullWidth required error={!!fieldErrors.address1} helperText={fieldErrors.address1 || ''} onChange={() => fieldErrors.address1 && setFieldErrors(p=>{ const c={...p}; delete c.address1; return c; })} />} /> */}
        {/* <FormField label="Billing Address 2" span="span 6" input={<TextField disabled={loading} name="address2" size="small" fullWidth onChange={() => fieldErrors.address2 && setFieldErrors(p=>{ const c={...p}; delete c.address2; return c; })} />} /> */}
        {/* <FormField label="City" span="span 2" input={<TextField disabled={loading} name="city" size="small" fullWidth required error={!!fieldErrors.city} helperText={fieldErrors.city || ''} onChange={() => fieldErrors.city && setFieldErrors(p=>{ const c={...p}; delete c.city; return c; })} />} /> */}
        <FormField label={t('country')} span="span 2" input={<TextField disabled={loading} name="country" size="small" fullWidth select value={country} required error={!!fieldErrors.country} helperText={fieldErrors.country || ''} onChange={e => { setCountry(e.target.value); setAdminArea(''); if(fieldErrors.country) setFieldErrors(p=>{ const c={...p}; delete c.country; return c; }); }}>
          <MenuItem value="US">United States</MenuItem>
          <MenuItem value="CA">Canada</MenuItem>
        </TextField>} />
        <FormField label={t('postalCode')} span="span 1" input={<TextField disabled={loading} name="postalCode" size="small" fullWidth required error={!!fieldErrors.postalCode} helperText={fieldErrors.postalCode || ''} onChange={() => fieldErrors.postalCode && setFieldErrors(p=>{ const c={...p}; delete c.postalCode; return c; })} />} />
        <Box sx={{ gridColumn:'1 / -1', mt:1, display:'flex', gap:2 }}>
          <Button type="submit" variant="contained" disabled={loading || verifying || !microformReady || paymentSuccess}>
            {verifying ? t('processing') : t('addCard')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
